<script setup lang="ts">
import { ref, computed, toRef } from 'vue';
import { message } from 'ant-design-vue';
import SiteCreateTile from './SiteCreateTile.vue';
import AddIcon from '@shared/assets/icon/add.svg';
import WarningBox from './WarningBox.vue';
import ButtonSecondary from '@/shared/components/button/ButtonSecondary.vue';
import { ProjectFormModel, SiteFormModel } from '../models/Project';

const props = withDefaults(
  defineProps<{
    project: ProjectFormModel;
    showProjectName?: boolean;
    disabled?: boolean;
    showPersistedSites?: boolean;
    deletePersistedSite?: boolean;
    editAddressPersistedSite?: boolean;
    projectNameLabel?: string;
    siteLabelWhenPersisted?: string;
    firstSiteLabel?: string;
    otherSiteLabel?: string;
    addSiteButtonLabel?: string;
  }>(),
  {
    showProjectName: true,
    disabled: false,
    showPersistedSites: false,
    deletePersistedSite: false,
    editAddressPersistedSite: false,
    projectNameLabel: 'Give this project a name',
    siteLabelWhenPersisted: 'Site address',
    firstSiteLabel: 'Enter a site address for this project',
    otherSiteLabel: 'Enter the next site address for this project',
    addSiteButtonLabel: 'Add another site to this project',
  },
);

const isCreateMode = computed(() => props.project.id === 0);
const project = toRef(props, 'project');
const projectName = computed({
  get: () => project.value.name ?? '',
  set: (value: string) => {
    project.value.name = value;
  },
});

const getNextSiteId = () => {
  const maxId = project.value.sites.reduce((currentMax, site) => {
    return Math.max(currentMax, site.id);
  }, 0);
  return maxId + 1;
};

const errorMessages = ref<{
  topLevel: Array<string>;
  siteLevel: Record<number, Array<string>>;
}>({
  topLevel: [],
  siteLevel: {},
});

const hasTopLevelError = computed(() => errorMessages.value.topLevel.length > 0);

const hasError = computed(
  () => hasTopLevelError.value || Object.values(errorMessages.value.siteLevel).flat().length > 0,
);

const resetTopLevelErrors = () => {
  errorMessages.value.topLevel = [];
};

const resetAllSiteLevelErrors = () => {
  errorMessages.value.siteLevel = {};
};

const resetAllErrors = () => {
  resetTopLevelErrors();
  resetAllSiteLevelErrors();
};

const resetSiteLevelErrorsAt = (siteId: number) => {
  errorMessages.value.siteLevel[siteId] = [];
};

const validateInput = () => {
  resetAllErrors();

  // Top Level: Validate that at least one site is added
  if (project.value.sites.length === 0) {
    errorMessages.value.topLevel.push('At least one site address is required.');
  }

  // Top Level: Validate that all sites are in the same country
  const [firstSite] = project.value.sites;
  const moreThanOneSite = project.value.sites.length > 1;
  const allSitesHaveCountry = project.value.sites.every((site) => site.country !== null);
  const baseCountry = firstSite?.country ?? null;
  if (
    moreThanOneSite &&
    allSitesHaveCountry &&
    project.value.sites.some((site) => site.country !== baseCountry)
  ) {
    errorMessages.value.topLevel.push('All sites must be in the same country.');
  }

  const __addSiteErrorMessage = (siteId: number, message: string) => {
    if (!errorMessages.value.siteLevel[siteId]) {
      errorMessages.value.siteLevel[siteId] = [];
    }
    errorMessages.value.siteLevel[siteId].push(message);
  };

  for (const [index, site] of displayedSites.value.entries()) {
    const isFirstSite = index === 0;

    // Site Level: Validate that the site has an address
    const isExistingSite = site.persisted;
    const siteAddressIsEmpty = !site.address || site.address.trim() === '';
    const sitePostcodeIsEmpty = !site.postcode || site.postcode.trim() === '';
    const siteCountryIsEmpty = !site.country || site.country.trim() === '';
    if (siteAddressIsEmpty) {
      if (isFirstSite) {
        __addSiteErrorMessage(site.id, 'Enter address for this site.');
      } else {
        __addSiteErrorMessage(site.id, 'Enter address or remove this site.');
      }
    } else {
      // Site Level: Validate that the site has a country and postcode
      // No need to check if the address is already empty
      if (!isExistingSite && sitePostcodeIsEmpty) {
        __addSiteErrorMessage(site.id, 'Enter postcode for this site.');
      }
      if (siteCountryIsEmpty) {
        __addSiteErrorMessage(site.id, 'Select country for this site.');
      }
    }

    // Site Level: Validate that the site has at least one SolShare
    const solshareCountIsZero = site.plannedSolshareCount === 0;
    if (solshareCountIsZero) {
      __addSiteErrorMessage(site.id, 'This site must have at least one SolShare.');
    }

    // Site Level: plannedSolshareCount should not be less than the commissionedSolshareCount
    if (
      site.commissionedSolshareCount && // Non-null and non-zero
      site.commissionedSolshareCount > 0 &&
      site.plannedSolshareCount < site.commissionedSolshareCount
    ) {
      __addSiteErrorMessage(
        site.id,
        `There's already ${site.commissionedSolshareCount} SolShare commissioned at this site.`,
      );
    }
  }

  let validationResult = true;
  if (hasError.value) {
    message.error('Please resolve the errors before continuing.');
    validationResult = false;
  }
  return validationResult;
};

const addSite = () => {
  const newSite = new SiteFormModel();
  newSite.id = getNextSiteId();
  newSite.persisted = false;
  project.value.sites.push(newSite);
};

const deleteSite = (id: number) => {
  resetTopLevelErrors();
  project.value.sites = project.value.sites.filter((site) => site.id !== id);
};

const updateSite = (site: SiteFormModel) => {
  resetTopLevelErrors();
  project.value.sites = project.value.sites.map((s: SiteFormModel) => {
    return s.id === site.id ? site : s;
  });
};

const displayedSites = computed(() => {
  return project.value.sites.filter((site) => (props.showPersistedSites ? true : !site.persisted));
});

defineExpose({ validateInput });
</script>

<template>
  <div
    :class="[
      'project-container',
      { 'project-container__extra-top-padding': !showProjectName && hasTopLevelError },
    ]"
  >
    <div
      :class="[
        'error-container',
        { 'error-container__extra-bottom-padding': showProjectName && hasTopLevelError },
      ]"
      v-if="hasTopLevelError"
    >
      <WarningBox type="error">
        <template #title>{{
          errorMessages.topLevel.length === 1
            ? errorMessages.topLevel[0]
            : 'Please fix the following errors:'
        }}</template>
        <template #message v-if="errorMessages.topLevel.length > 1">
          <ul>
            <li v-for="(error, index) in errorMessages.topLevel" :key="index">{{ error }}</li>
          </ul>
        </template>
      </WarningBox>
    </div>
    <div class="name-container section-divider" v-if="showProjectName">
      <a-form-item name="name">
        <template #label>
          <span>{{ projectNameLabel }}</span>
        </template>
        <a-input
          v-model:value="projectName"
          placeholder="Enter project name"
          size="large"
          data-tn="project-name-input"
          :disabled="disabled"
        />
      </a-form-item>
    </div>
    <div class="sites-container">
      <div
        v-for="(site, index) in displayedSites"
        :key="site.id"
        class="site-wrapper section-divider"
      >
        <SiteCreateTile
          :title="
            site.persisted ? siteLabelWhenPersisted : index === 0 ? firstSiteLabel : otherSiteLabel
          "
          :id="site.id"
          :site="site"
          :projectCountry="project.country"
          :errorMessages="
            errorMessages.siteLevel[site.id] ? errorMessages.siteLevel[site.id] : null
          "
          @deleteSite="() => deleteSite(site.id)"
          @updateSite="(value) => updateSite(value)"
          :showDeleteIcon="
            index > 0 && (isCreateMode || !site.persisted || props.deletePersistedSite)
          "
          :disabledAddress="disabled || (!editAddressPersistedSite && site.persisted)"
          :disabledNumberOfSolShares="disabled"
          @onTouch="() => resetSiteLevelErrorsAt(site.id)"
        />
      </div>
    </div>
    <div class="add-site-container">
      <ButtonSecondary block @click="addSite" data-tn="add-site-btn" :disabled="disabled">
        <template #iconLeft>
          <AddIcon />
        </template>
        {{ addSiteButtonLabel }}
      </ButtonSecondary>
    </div>
  </div>
</template>

<style lang="less" scoped>
.project-container {
  width: 100%;
  &__extra-top-padding {
    padding-top: @content-side-margin;
  }
}

.section-divider {
  padding: @content-side-margin;
  width: 100%;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: -@content-side-margin;
    right: -@content-side-margin;
    bottom: 0;
    border-bottom: 1px solid @color-mid-tone;
  }
}

.error-container {
  padding: 0;
  &__extra-bottom-padding {
    padding-bottom: @content-side-margin;
  }
}

.name-container {
  padding: 0 0 @content-side-margin;

  :deep(.ant-form-item) {
    margin-bottom: 10px;
    width: 100%;
  }

  :deep(.ant-form-item-label) {
    padding-bottom: 0;
    width: 100%;
    text-align: start;
  }
  :deep(.ant-form-item-label label) {
    color: @color-primary;
  }
}

.sites-container {
  width: 100%;

  .site-wrapper {
    padding: @content-side-margin 0;
  }
}

.add-site-container {
  padding: @content-side-margin 0 0;
  width: 100%;
}
</style>
